import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar, DollarSign, Clock, CheckCircle, XCircle,
  ChevronDown, Trash2, Plus, BookOpen, Lock, Music2,
  Star, MessageSquare, Edit2, Save, X, Image, TrendingUp, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";
const ACCENT = '#EF0000';
const BG = 'linear-gradient(135deg, #0a0000, #1a0508, #0d0d0d)';

// Create a dedicated axios instance for DJ Admin to avoid conflicts with AuthContext
const djApi = axios.create({ baseURL: API });
const setDjAuth = (t) => { djApi.defaults.headers.common['Authorization'] = `Bearer ${t}`; };

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#111] border border-white/10 rounded-xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[#A1A1AA] text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-[#EDEDED]">{value}</p>
  </div>
);

// ===== Bookings Tab =====
const BookingsTab = ({ bookings, onStatusUpdate, onDelete }) => {
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm capitalize transition-all ${
              filter === s ? 'text-white' : 'bg-[#1a1a1a] text-[#A1A1AA] border border-white/10 hover:text-white'
            }`}
            style={filter === s ? { background: ACCENT } : {}}>
            {s} {s !== 'all' && `(${bookings.filter(b => b.status === s).length})`}
            {s === 'all' && `(${bookings.length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No bookings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(booking => (
            <div key={booking.id} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 cursor-pointer flex items-center justify-between"
                onClick={() => setExpandedId(expandedId === booking.id ? null : booking.id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#EDEDED] font-medium">{booking.user_name}</span>
                      <span className="text-[#52525B]">-</span>
                      <span className="text-sm capitalize" style={{ color: ACCENT }}>{booking.event_type?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-[#A1A1AA]">
                      <span>{new Date(booking.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className={`capitalize px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[booking.status]}`}>{booking.status}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: ACCENT }}>£{booking.total_price}</p>
                    <p className="text-xs text-[#A1A1AA] capitalize">{booking.package_tier}</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-[#A1A1AA] ml-3 transition-transform ${expandedId === booking.id ? 'rotate-180' : ''}`} />
              </div>

              {expandedId === booking.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  className="border-t border-white/10 p-4 bg-[#0a0a0a]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><p className="text-[#A1A1AA]">Email</p><p className="text-[#EDEDED]">{booking.user_email}</p></div>
                    <div><p className="text-[#A1A1AA]">Phone Number</p><p className="text-[#EDEDED]">{booking.phone_number || 'Not provided'}</p></div>
                    <div><p className="text-[#A1A1AA]">Start Time</p><p className="text-[#EDEDED]">{booking.start_time || 'Not specified'}</p></div>
                    <div><p className="text-[#A1A1AA]">End Time</p><p className="text-[#EDEDED]">{booking.end_time || 'Not specified'}</p></div>
                    <div><p className="text-[#A1A1AA]">Location</p><p className="text-[#EDEDED]">{booking.location || 'Not specified'}</p></div>
                    <div><p className="text-[#A1A1AA]">Venue</p><p className="text-[#EDEDED]">{booking.venue || 'Not specified'}</p></div>
                    <div><p className="text-[#A1A1AA]">Expected Guests</p><p className="text-[#EDEDED]">{booking.expected_guests || 'Not specified'}</p></div>
                    <div><p className="text-[#A1A1AA]">Package</p><p className="text-[#EDEDED] capitalize">{booking.package_tier}</p></div>
                    {booking.music_preferences?.length > 0 && (
                      <div className="md:col-span-2">
                        <p className="text-[#A1A1AA] mb-1">Music Preferences</p>
                        <div className="flex flex-wrap gap-1">
                          {booking.music_preferences.map(g => (
                            <span key={g} className="px-2 py-0.5 rounded-full text-xs border" style={{ borderColor: ACCENT + '4d', color: ACCENT, background: ACCENT + '1a' }}>{g}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {booking.let_dj_choose && <div className="md:col-span-2"><p className="text-[#A1A1AA] italic text-xs">Customer prefers J Major to choose the music.</p></div>}
                    {booking.special_request && <div className="md:col-span-2"><p className="text-[#A1A1AA]">Special Request</p><p className="text-[#EDEDED]">{booking.special_request}</p></div>}
                    {booking.first_dance_song && <div className="md:col-span-2"><p className="text-[#A1A1AA]">First Dance / Entrance Song</p><p className="text-[#EDEDED]">{booking.first_dance_song}</p></div>}
                    {booking.notes && <div className="md:col-span-2"><p className="text-[#A1A1AA]">Notes</p><p className="text-[#EDEDED]">{booking.notes}</p></div>}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <div>
                      <p className="text-[#A1A1AA] text-sm mb-2">Update Status:</p>
                      <div className="flex flex-wrap gap-2">
                        {['pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                          <button key={status} onClick={() => onStatusUpdate(booking.id, status)}
                            disabled={booking.status === status}
                            className={`px-3 py-1.5 rounded-lg text-sm capitalize border transition-all ${
                              booking.status === status ? STATUS_COLORS[status] + ' font-medium' : 'bg-[#1a1a1a] text-[#A1A1AA] border-white/10 hover:text-white'
                            }`}>{status}</button>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => onDelete(booking.id)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                  <p className="text-xs text-[#52525B] mt-3">Booked on: {new Date(booking.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Earnings Tab =====
const EarningsTab = ({ stats }) => {
  const monthly = stats?.monthly_earnings || [];
  const maxRevenue = Math.max(...monthly.map(m => m.revenue), 1);

  return (
    <div>
      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#111] border border-white/10 rounded-xl p-6 text-center">
          <DollarSign className="w-8 h-8 mx-auto mb-2" style={{ color: ACCENT }} />
          <p className="text-[#A1A1AA] text-sm mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-[#EDEDED]">£{stats?.total_revenue || 0}</p>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-6 text-center">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
          <p className="text-[#A1A1AA] text-sm mb-1">Completed</p>
          <p className="text-3xl font-bold text-[#EDEDED]">{stats?.completed || 0}</p>
        </div>
        <div className="bg-[#111] border border-white/10 rounded-xl p-6 text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-blue-400" />
          <p className="text-[#A1A1AA] text-sm mb-1">Confirmed</p>
          <p className="text-3xl font-bold text-[#EDEDED]">{stats?.confirmed || 0}</p>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-[#111] border border-white/10 rounded-xl p-6">
        <h3 className="text-[#EDEDED] font-medium mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" style={{ color: ACCENT }} /> Monthly Earnings
        </h3>
        {monthly.length === 0 ? (
          <p className="text-center py-8 text-[#52525B] text-sm italic">No earnings data yet</p>
        ) : (
          <div className="space-y-4">
            {monthly.map(m => (
              <div key={m.month} className="flex items-center gap-4">
                <span className="text-[#A1A1AA] text-sm w-24 flex-shrink-0">{m.month}</span>
                <div className="flex-1 bg-[#1a1a1a] rounded-full h-6 overflow-hidden">
                  <div className="h-full rounded-full flex items-center px-3" style={{ width: `${Math.max((m.revenue / maxRevenue) * 100, 8)}%`, background: ACCENT }}>
                    <span className="text-white text-xs font-medium whitespace-nowrap">£{m.revenue}</span>
                  </div>
                </div>
                <span className="text-[#52525B] text-xs w-20 text-right">{m.bookings} booking{m.bookings !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Packages Tab =====
const PackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newPkg, setNewPkg] = useState({ name: '', tier: '', price: 0, duration: '', tagline: '', features: '', highlight: false });

  const fetchPackages = async () => {
    try { const res = await djApi.get('/packages/dj'); setPackages(res.data.packages); }
    catch { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPackages(); }, []);

  const addPackage = async () => {
    if (!newPkg.name || !newPkg.tier || !newPkg.price) { toast.error('Name, tier, and price required'); return; }
    try {
      await djApi.post('/admin/packages/dj', {
        ...newPkg,
        price: Number(newPkg.price),
        features: newPkg.features.split('\n').filter(f => f.trim()),
        sort_order: packages.length
      });
      toast.success('Package added!');
      setNewPkg({ name: '', tier: '', price: 0, duration: '', tagline: '', features: '', highlight: false });
      setShowAdd(false);
      fetchPackages();
    } catch { toast.error('Failed to add'); }
  };

  const saveEdit = async (id) => {
    try {
      const payload = { ...editForm, price: Number(editForm.price), features: typeof editForm.features === 'string' ? editForm.features.split('\n').filter(f => f.trim()) : editForm.features };
      await djApi.put(`/admin/packages/dj/${id}`, payload);
      toast.success('Updated!');
      setEditingId(null);
      fetchPackages();
    } catch { toast.error('Failed to update'); }
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try { await djApi.delete(`/admin/packages/dj/${id}`); toast.success('Deleted'); fetchPackages(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <p className="text-center py-12 text-[#A1A1AA]">Loading...</p>;

  return (
    <div>
      <div className="mb-6">
        <Button onClick={() => setShowAdd(!showAdd)} className="rounded-lg text-sm flex items-center gap-2 text-white" style={{ background: ACCENT }}>
          <Plus className="w-4 h-4" /> Add Package
        </Button>
      </div>

      {showAdd && (
        <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6">
          <h3 className="text-[#EDEDED] font-medium mb-4">New DJ Package</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={newPkg.name} onChange={e => setNewPkg({ ...newPkg, name: e.target.value })} placeholder="Package name" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
            <input value={newPkg.tier} onChange={e => setNewPkg({ ...newPkg, tier: e.target.value })} placeholder="Tier key (e.g. premium)" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
            <input type="number" value={newPkg.price} onChange={e => setNewPkg({ ...newPkg, price: e.target.value })} placeholder="Price" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
            <input value={newPkg.duration} onChange={e => setNewPkg({ ...newPkg, duration: e.target.value })} placeholder="Duration (e.g. Up to 10 Hours)" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
          </div>
          <input value={newPkg.tagline} onChange={e => setNewPkg({ ...newPkg, tagline: e.target.value })} placeholder="Tagline" className="mt-3 w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
          <textarea value={newPkg.features} onChange={e => setNewPkg({ ...newPkg, features: e.target.value })} placeholder="Features (one per line)" rows={4} className="mt-3 w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
          <label className="flex items-center gap-2 mt-3 text-sm text-[#A1A1AA]">
            <input type="checkbox" checked={newPkg.highlight} onChange={e => setNewPkg({ ...newPkg, highlight: e.target.checked })} className="accent-[#EF0000]" /> Mark as highlighted/popular
          </label>
          <div className="flex gap-2 mt-4">
            <Button onClick={addPackage} className="rounded-lg text-sm text-white" style={{ background: ACCENT }}>Save Package</Button>
            <Button onClick={() => setShowAdd(false)} variant="outline" className="rounded-lg text-sm border-white/10 text-[#A1A1AA]">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {packages.map(pkg => (
          <div key={pkg.id} className="bg-[#111] border border-white/10 rounded-xl p-5">
            {editingId === pkg.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                  <input type="number" value={editForm.price} onChange={e => setEditForm({ ...editForm, price: e.target.value })} className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                </div>
                <input value={editForm.duration} onChange={e => setEditForm({ ...editForm, duration: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                <textarea value={typeof editForm.features === 'string' ? editForm.features : editForm.features?.join('\n')} onChange={e => setEditForm({ ...editForm, features: e.target.value })} rows={4} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(pkg.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-green-600/20 text-green-400 border border-green-500/30"><Save className="w-3 h-3" /> Save</button>
                  <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-[#1a1a1a] text-[#A1A1AA] border border-white/10"><X className="w-3 h-3" /> Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-[#EDEDED] font-medium">{pkg.name}</h3>
                    <span className="text-lg font-bold" style={{ color: ACCENT }}>£{pkg.price}</span>
                    {pkg.highlight && <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ background: ACCENT }}>Popular</span>}
                  </div>
                  <p className="text-[#52525B] text-xs">{pkg.duration} - {pkg.tagline}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pkg.features?.map((f, i) => <span key={i} className="text-xs text-[#A1A1AA] bg-[#1a1a1a] px-2 py-0.5 rounded">{f}</span>)}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditingId(pkg.id); setEditForm({ name: pkg.name, price: pkg.price, duration: pkg.duration, tagline: pkg.tagline, features: pkg.features, highlight: pkg.highlight }); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EF0000] border border-white/10"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => deletePackage(pkg.id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-red-400 border border-white/10"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Portfolio Tab =====
const PortfolioTab = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newCaption, setNewCaption] = useState('');

  const fetchImages = async () => {
    try { const res = await djApi.get('/dj/portfolio'); setImages(res.data.images); }
    catch { toast.error('Failed to load portfolio'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchImages(); }, []);

  const addImage = async () => {
    if (!newUrl) { toast.error('Image URL required'); return; }
    try {
      await djApi.post('/admin/dj/portfolio', { image_url: newUrl, caption: newCaption });
      toast.success('Image added!');
      setNewUrl('');
      setNewCaption('');
      fetchImages();
    } catch { toast.error('Failed to add'); }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try { await djApi.delete(`/admin/dj/portfolio/${id}`); toast.success('Deleted'); fetchImages(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <p className="text-center py-12 text-[#A1A1AA]">Loading...</p>;

  return (
    <div>
      <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6">
        <h3 className="text-[#EDEDED] font-medium mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" style={{ color: ACCENT }} /> Add Photo to Portfolio
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="Paste image URL..." className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm flex-1" data-testid="dj-portfolio-url" />
          <input value={newCaption} onChange={e => setNewCaption(e.target.value)} placeholder="Caption (optional)" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm flex-1" data-testid="dj-portfolio-caption" />
          <Button onClick={addImage} className="rounded-lg text-sm text-white" style={{ background: ACCENT }} data-testid="dj-portfolio-add">Add</Button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]"><Image className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No portfolio images yet</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map(img => (
            <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#1a1a1a] border border-white/10">
              <img src={img.image_url} alt={img.caption || ''} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                {img.caption && <p className="text-white text-xs px-2 text-center">{img.caption}</p>}
                <button onClick={() => deleteImage(img.id)} className="px-3 py-1 rounded-lg text-xs bg-red-600/80 text-white flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Reviews Tab =====
const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newReview, setNewReview] = useState({ user_name: '', rating: 5, comment: '', event_type: '' });

  const fetchReviews = async () => {
    try { const res = await djApi.get('/admin/reviews?type=dj'); setReviews(res.data); }
    catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReviews(); }, []);

  const addReview = async () => {
    if (!newReview.user_name || !newReview.comment) { toast.error('Name and comment required'); return; }
    try { await djApi.post('/admin/reviews?type=dj', newReview); toast.success('Review added!'); setNewReview({ user_name: '', rating: 5, comment: '', event_type: '' }); setShowAdd(false); fetchReviews(); }
    catch { toast.error('Failed to add'); }
  };

  const saveEdit = async (id) => {
    try { await djApi.put(`/admin/reviews/${id}`, editForm); toast.success('Updated!'); setEditingId(null); fetchReviews(); }
    catch { toast.error('Failed to update'); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await djApi.delete(`/admin/reviews/${id}`); toast.success('Deleted'); fetchReviews(); }
    catch { toast.error('Failed to delete'); }
  };

  const renderStars = (rating, editable = false, onChange = null) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${editable ? 'cursor-pointer' : ''} ${i <= rating ? 'fill-current' : 'text-[#52525B]'}`}
          style={i <= rating ? { color: ACCENT } : {}} onClick={editable ? () => onChange(i) : undefined} />
      ))}
    </div>
  );

  if (loading) return <p className="text-center py-12 text-[#A1A1AA]">Loading...</p>;

  return (
    <div>
      <div className="mb-6">
        <Button onClick={() => setShowAdd(!showAdd)} className="rounded-lg text-sm flex items-center gap-2 text-white" style={{ background: ACCENT }}>
          <Plus className="w-4 h-4" /> Add Review
        </Button>
      </div>
      {showAdd && (
        <div className="bg-[#111] border border-white/10 rounded-xl p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input value={newReview.user_name} onChange={e => setNewReview({ ...newReview, user_name: e.target.value })} placeholder="Customer name" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
            <input value={newReview.event_type} onChange={e => setNewReview({ ...newReview, event_type: e.target.value })} placeholder="Event type" className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
          </div>
          <div className="mt-3">{renderStars(newReview.rating, true, r => setNewReview({ ...newReview, rating: r }))}</div>
          <textarea value={newReview.comment} onChange={e => setNewReview({ ...newReview, comment: e.target.value })} placeholder="Review comment..." rows={3} className="mt-3 w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
          <div className="flex gap-2 mt-4">
            <Button onClick={addReview} className="rounded-lg text-sm text-white" style={{ background: ACCENT }}>Save</Button>
            <Button onClick={() => setShowAdd(false)} variant="outline" className="rounded-lg text-sm border-white/10 text-[#A1A1AA]">Cancel</Button>
          </div>
        </div>
      )}
      {reviews.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]"><MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No reviews yet</p></div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.id} className="bg-[#111] border border-white/10 rounded-xl p-4">
              {editingId === review.id ? (
                <div className="space-y-3">
                  <input value={editForm.user_name} onChange={e => setEditForm({ ...editForm, user_name: e.target.value })} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                  <div>{renderStars(editForm.rating, true, r => setEditForm({ ...editForm, rating: r }))}</div>
                  <textarea value={editForm.comment} onChange={e => setEditForm({ ...editForm, comment: e.target.value })} rows={2} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(review.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-green-600/20 text-green-400 border border-green-500/30"><Save className="w-3 h-3" /> Save</button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-[#1a1a1a] text-[#A1A1AA] border border-white/10"><X className="w-3 h-3" /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[#EDEDED] font-medium">{review.user_name}</span>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-[#A1A1AA] text-sm">{review.comment}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingId(review.id); setEditForm({ user_name: review.user_name, rating: review.rating, comment: review.comment }); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EF0000] border border-white/10"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteReview(review.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] text-[#A1A1AA] hover:text-red-400 border border-white/10"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Main DJ Admin Component =====
export default function DJAdmin() {
  const [activeTab, setActiveTab] = useState('bookings');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('dj_admin_token') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (token) {
      setDjAuth(token);
      setIsLoggedIn(true);
      fetchData();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (!res.data.user?.is_admin) { setLoginError('This account does not have admin access.'); setLoginLoading(false); return; }
      const t = res.data.access_token;
      localStorage.setItem('dj_admin_token', t);
      setDjAuth(t);
      setToken(t);
      setIsLoggedIn(true);
    } catch (err) { setLoginError(err.response?.data?.detail || 'Invalid email or password'); }
    finally { setLoginLoading(false); }
  };

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        djApi.get('/admin/dj/stats'),
        djApi.get('/admin/dj/bookings'),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setIsLoggedIn(false);
        localStorage.removeItem('dj_admin_token');
      }
    } finally { setDataLoading(false); }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await djApi.put(`/admin/dj/bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
      const statsRes = await djApi.get('/admin/dj/stats');
      setStats(statsRes.data);
    } catch { toast.error('Failed to update status'); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try { await djApi.delete(`/admin/dj/bookings/${id}`); toast.success('Booking deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  if (token && dataLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" style={{ background: BG }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: ACCENT }} />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: BG }} data-testid="dj-admin-login-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: ACCENT + '1a' }}>
                <Lock className="w-7 h-7" style={{ color: ACCENT }} />
              </div>
              <img src={JMAJOR_LOGO} alt="J MAJOR" className="h-10 mx-auto mb-3" />
              <h1 className="font-['Playfair_Display'] text-2xl text-[#EDEDED]">DJ Admin</h1>
              <p className="text-[#A1A1AA] text-sm mt-1">Sign in to manage DJ bookings</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter admin email" required
                  className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-[#EDEDED] placeholder:text-[#52525B] focus:outline-none"
                  data-testid="dj-admin-email" />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required
                    className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-[#EDEDED] placeholder:text-[#52525B] focus:outline-none pr-12"
                    data-testid="dj-admin-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]">
                    {showPassword ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
              <Button type="submit" disabled={loginLoading} className="w-full py-6 text-white" style={{ background: ACCENT }} data-testid="dj-admin-submit">
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'earnings', label: 'Earnings', icon: DollarSign },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'portfolio', label: 'Portfolio', icon: Image },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: BG }} data-testid="dj-admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <img src={JMAJOR_LOGO} alt="J MAJOR" className="h-8" />
              <h1 className="text-3xl font-['Playfair_Display'] text-[#EDEDED]">
                DJ <span style={{ color: ACCENT }}>Admin</span>
              </h1>
            </div>
            <p className="text-[#A1A1AA] text-sm">Manage your DJ bookings, earnings, packages, and portfolio</p>
          </div>
          <Button onClick={() => { localStorage.removeItem('dj_admin_token'); setIsLoggedIn(false); setToken(''); delete djApi.defaults.headers.common['Authorization']; }}
            variant="outline" className="border-white/10 text-[#A1A1AA] rounded-full text-sm">Logout</Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Calendar} label="Total Bookings" value={stats.total_bookings} color="bg-[#EF0000]/20 text-[#EF0000]" />
            <StatCard icon={Clock} label="Pending" value={stats.pending} color="bg-yellow-500/20 text-yellow-400" />
            <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed} color="bg-blue-500/20 text-blue-400" />
            <StatCard icon={DollarSign} label="Revenue" value={`£${stats.total_revenue || 0}`} color="bg-green-500/20 text-green-400" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'text-white' : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
              }`}
              style={activeTab === tab.id ? { background: ACCENT } : {}}
              data-testid={`dj-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'bookings' && <BookingsTab bookings={bookings} onStatusUpdate={updateBookingStatus} onDelete={deleteBooking} />}
        {activeTab === 'earnings' && <EarningsTab stats={stats} />}
        {activeTab === 'packages' && <PackagesTab />}
        {activeTab === 'portfolio' && <PortfolioTab />}
        {activeTab === 'reviews' && <ReviewsTab />}
      </div>
    </div>
  );
}
